/**
 * UsageSummaryCards Component
 *
 * Displays token usage summary metrics in a card-based layout.
 * Shows total tokens, total cost, API calls, and budget status (if configured).
 *
 * Technical Design Reference: docs/technical/TD-AC-594-token-audit.md
 * Related Tickets:
 * - AC-594 (Epic: Token Audit System)
 * - AC-605 (Frontend: UsageSummaryCards component)
 * - AC-612 (Parent: UsageDashboard container)
 */

import React from 'react';
import { Coins, DollarSign, Activity, PiggyBank, AlertCircle } from 'lucide-react';
import type { TokenUsageSummary } from '../../types/tokenUsage';

export interface UsageSummaryCardsProps {
  /** Token usage summary data from API */
  summary: TokenUsageSummary | null;
  /** Whether data is currently loading */
  loading?: boolean;
  /** Error message to display */
  error?: string | null;
}

/**
 * Format a number with thousands separator using locale formatting.
 */
const formatNumber = (n: number): string => {
  return n.toLocaleString('en-US');
};

/**
 * Format a number as currency with $ symbol.
 * Uses adaptive precision: 4 decimal places for amounts under $1,
 * 2 decimal places for larger amounts.
 * For large numbers, includes thousands separator.
 */
const formatCurrency = (n: number): string => {
  // Use more precision for small amounts (common in token costs)
  const decimalPlaces = n < 1 && n > 0 ? 4 : 2;
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}`;
};

/**
 * Loading skeleton for a single card.
 */
const CardSkeleton: React.FC = () => (
  <div
    data-testid="usage-card-skeleton"
    className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 animate-pulse"
  >
    <div className="flex items-center">
      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg" />
      <div className="ml-4 flex-1">
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2" />
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16" />
      </div>
    </div>
  </div>
);

/**
 * Loading state showing skeleton placeholders for all cards.
 */
const LoadingState: React.FC = () => (
  <div
    role="status"
    aria-busy="true"
    aria-label="Loading usage data"
    data-testid="loading-skeleton"
    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
  >
    <CardSkeleton />
    <CardSkeleton />
    <CardSkeleton />
    <CardSkeleton />
  </div>
);

/**
 * Error state showing error message with icon.
 */
const ErrorState: React.FC<{ message: string }> = ({ message }) => (
  <div
    role="alert"
    className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 flex items-center"
  >
    <AlertCircle
      data-testid="usage-error-icon"
      className="w-6 h-6 text-red-500 mr-3 flex-shrink-0"
    />
    <span className="text-red-700 dark:text-red-400">{message}</span>
  </div>
);

/**
 * Empty state when no data is available.
 */
const EmptyState: React.FC = () => (
  <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-6 text-center">
    <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
    <p className="text-gray-500 dark:text-gray-400">No usage data available</p>
  </div>
);

/**
 * Determine budget card border color based on usage percentage.
 */
const getBudgetBorderColor = (percentageUsed: number): string => {
  if (percentageUsed >= 90) {
    return 'border-red-500';
  }
  if (percentageUsed >= 75) {
    return 'border-yellow-500';
  }
  return 'border-green-500';
};

/**
 * UsageSummaryCards component displays key token usage metrics.
 */
const UsageSummaryCards: React.FC<UsageSummaryCardsProps> = ({
  summary,
  loading = false,
  error = null,
}) => {
  // Show loading state
  if (loading) {
    return <LoadingState />;
  }

  // Show error state
  if (error) {
    return <ErrorState message={error} />;
  }

  // Show empty state when no summary data
  if (!summary) {
    return <EmptyState />;
  }

  return (
    <div
      data-testid="usage-summary-cards-container"
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
    >
      {/* Total Tokens Card */}
      <div
        data-testid="total-tokens-card"
        className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-blue-500"
      >
        <div className="flex items-center">
          <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
            <Coins
              data-testid="total-tokens-icon"
              className="w-6 h-6 text-primary-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Tokens</h3>
            <p
              className="text-2xl font-bold text-gray-900 dark:text-warm-white"
              aria-label="total tokens value"
            >
              {formatNumber(summary.total_tokens)}
            </p>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
          <div className="flex justify-between">
            <span>Prompt:</span>
            <span>{formatNumber(summary.prompt_tokens)}</span>
          </div>
          <div className="flex justify-between">
            <span>Completion:</span>
            <span>{formatNumber(summary.completion_tokens)}</span>
          </div>
        </div>
      </div>

      {/* Total Cost Card */}
      <div
        data-testid="total-cost-card"
        className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-green-500"
      >
        <div className="flex items-center">
          <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-lg">
            <DollarSign
              data-testid="total-cost-icon"
              className="w-6 h-6 text-green-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Cost</h3>
            <p
              className="text-2xl font-bold text-gray-900 dark:text-warm-white"
              aria-label="total cost value"
            >
              {formatCurrency(summary.total_cost)}
            </p>
          </div>
        </div>
      </div>

      {/* API Calls Card */}
      <div
        data-testid="api-calls-card"
        className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 border-purple-500"
      >
        <div className="flex items-center">
          <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
            <Activity
              data-testid="api-calls-icon"
              className="w-6 h-6 text-purple-600"
            />
          </div>
          <div className="ml-4">
            <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">API Calls</h3>
            <p
              className="text-2xl font-bold text-gray-900 dark:text-warm-white"
              aria-label="api calls value"
            >
              {formatNumber(summary.api_call_count)}
            </p>
          </div>
        </div>
      </div>

      {/* Budget Status Card (only if budget is configured) */}
      {summary.budget && (
        <div
          data-testid="budget-status-card"
          className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 border-l-4 ${getBudgetBorderColor(summary.budget.percentage_used)}`}
        >
          <div className="flex items-center">
            <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
              <PiggyBank
                data-testid="budget-icon"
                className="w-6 h-6 text-orange-600"
              />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">Budget Status</h3>
              <p
                className="text-2xl font-bold text-gray-900 dark:text-warm-white"
                aria-label="budget usage percentage"
              >
                {summary.budget.percentage_used.toFixed(1)}%
              </p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 text-xs text-gray-500 dark:text-gray-400 space-y-1">
            <div className="flex justify-between">
              <span>Remaining:</span>
              <span>{formatCurrency(summary.budget.monthly_limit - summary.budget.current_spend)}</span>
            </div>
            <div className="flex justify-between">
              <span>Projected:</span>
              <span>{formatCurrency(summary.budget.projected_end_of_month)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageSummaryCards;
